import { uploadToSeaweedFS, formatFileSize } from '@/lib/seaweedfs';
import { compressImageFile } from '@/lib/imageCompress';

const MAX_IMAGE_BYTES = 6 * 1024 * 1024;
const MAX_VIDEO_BYTES = 15 * 1024 * 1024;
const MAX_DIGITAL_BYTES = 200 * 1024 * 1024;
const MAX_COURSE_VIDEO_BYTES = 300 * 1024 * 1024;

/**
 * Allège une image AVANT l'envoi : redimensionnement raisonnable + conversion
 * dans un format moderne, jusqu'à passer sous la cible, sans dégrader
 * visiblement la qualité. Les vidéos, GIF animés et fichiers non-image
 * passent tels quels.
 */
export async function prepareUploadFile(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif') return file; // conserve l'animation
  try {
    const compressed = await compressImageFile(file, {
      maxBytes: 1500 * 1024,
      maxDimension: 2200,
      mimeType: 'image/webp',
    });
    return compressed.size < file.size ? compressed : file;
  } catch {
    return file;
  }
}

// Identifiant de l'utilisateur connecté (session locale) : sert à ranger tous
// ses envois sous son dossier personnel `users/{id}/` afin que le poids de
// CHAQUE fichier uploadé soit bien compté dans son quota de stockage.
function currentUserId(): number | null {
  try {
    const raw = localStorage.getItem('zifek_user');
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { id?: unknown };
    const id = Number(parsed?.id);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

function scopedFolder(folder: string): string {
  const uid = currentUserId();
  return uid ? `users/${uid}/${folder}` : folder;
}

export async function uploadMediaFile(file: File, folder: string): Promise<string> {
  const isVideo = file.type.startsWith("video/");
  const maxSize = isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
  const limitLabel = isVideo ? "15 Mo" : "6 Mo";
  const actualSizeMB = (file.size / (1024 * 1024)).toFixed(1);

  if (file.size > maxSize) {
    throw new Error(
      `${isVideo ? "La video" : "L'image"} depasse la limite de ${limitLabel} (${actualSizeMB} Mo)`
    );
  }

  const ready = await prepareUploadFile(file);
  const arrayBuffer = await ready.arrayBuffer();
  const uint8 = new Uint8Array(arrayBuffer);

  return uploadToSeaweedFS(uint8, ready.name, ready.type, scopedFolder(folder));
}

/**
 * Téléverse une vidéo de cours (superadmin) avec une limite élevée.
 * Range le fichier dans le dossier `formations/videos`.
 */
export async function uploadCourseVideo(file: File): Promise<string> {
  if (!file.type.startsWith('video/')) {
    throw new Error('Le fichier sélectionné n\u2019est pas une vidéo.');
  }
  if (file.size > MAX_COURSE_VIDEO_BYTES) {
    throw new Error(`La vidéo dépasse la limite de 300 Mo (${formatFileSize(file.size)}).`);
  }
  const arrayBuffer = await file.arrayBuffer();
  const uint8 = new Uint8Array(arrayBuffer);
  return uploadToSeaweedFS(uint8, file.name, file.type || 'video/mp4', 'formations/videos');
}

export { formatFileSize } from '@/lib/seaweedfs';

export const DIGITAL_FILE_LIMIT_LABEL = '200 Mo';

/**
 * Téléverse un fichier digital (livre, vidéo, PDF, ZIP…) à livrer à l'acheteur.
 * Aucune restriction de type : seuls les fichiers très volumineux sont refusés.
 */
export async function uploadDigitalFile(file: File): Promise<string> {
  if (file.size > MAX_DIGITAL_BYTES) {
    throw new Error(`Le fichier dépasse la limite de ${DIGITAL_FILE_LIMIT_LABEL} (${formatFileSize(file.size)})`);
  }

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-80) || 'fichier';
  const arrayBuffer = await file.arrayBuffer();
  const uint8 = new Uint8Array(arrayBuffer);
  return uploadToSeaweedFS(uint8, safeName, file.type || 'application/octet-stream', scopedFolder('digital-files'));
}

export const MEDIA_LIMITS = {
  image: { maxBytes: MAX_IMAGE_BYTES, label: "6 Mo", accept: "image/jpeg,image/png,image/webp,image/gif" },
  video: { maxBytes: MAX_VIDEO_BYTES, label: "15 Mo", accept: "video/mp4,video/webm" },
};

// Limite globale pour la médiathèque centrale (tous types confondus).
const MAX_LIBRARY_BYTES = 50 * 1024 * 1024;

/**
 * Téléverse un fichier vers le stockage Zifek pour la médiathèque centrale.
 * Accepte tous types de médias (image, vidéo, audio, PDF, document).
 */
export async function uploadLibraryFile(file: File, folder: string): Promise<string> {
  if (file.size > MAX_LIBRARY_BYTES) {
    throw new Error(`Le fichier « ${file.name} » dépasse la limite de 50 Mo (${formatFileSize(file.size)}).`);
  }
  const ready = await prepareUploadFile(file);
  const arrayBuffer = await ready.arrayBuffer();
  const uint8 = new Uint8Array(arrayBuffer);
  return uploadToSeaweedFS(uint8, ready.name, ready.type || 'application/octet-stream', scopedFolder(folder));
}